/**
 * The delivery-job printout — the sheet of cards, laid out for Letter paper.
 *
 * This is the half of the retired `packagingToHtml` that was layout rather
 * than markup: a two-column 207mm grid of fixed 101×50mm cards, optionally
 * broken onto a fresh page per driver.
 *
 * The sheet is SELF-CONTAINED: it renders into a separate window whose
 * document links none of the app's CSS (#1902), so it carries its own
 * `<style>` for the handful of cut-line rules an inline style cannot express.
 * Everything else is inline, on the element that owns it.
 *
 * The geometry below is transcribed from that stylesheet and must stay in
 * absolute units. Letter is 215.9mm wide; minus the 4mm side margins the
 * printable width is 207.9mm, against which two 101mm cards plus a 5mm gutter
 * (207mm) leave 0.9mm of slack. Re-expressing any of it in rem or Tailwind
 * spacing scales it with a font size and overflows the page.
 */
import { useTranslation } from 'react-i18next';
import PackagingCard, { PACKAGING_CARD_COLUMN_GAP, PACKAGING_CARD_WIDTH } from './PackagingCard';
import { PACKAGING_SHEET_CSS } from './packagingPrintStyles';
import type { PackagingCustomerEntry, PackagingResult } from './packagingData';

interface PackagingSheetProps {
  result: PackagingResult;
  /** Start each driver's cards on a fresh sheet of paper. */
  pageBreakPerDriver: boolean;
}

/**
 * Cards per printed sheet: the grid is 2 columns x 5 rows of the fixed
 * 101 x 50mm card, i.e. 207 x 250mm, which fits Letter (215.9 x 279.4mm) at
 * the `@page` margins the print action declares (15mm top, 14mm bottom, 4mm
 * sides — see `jobResource.tsx`) with 0.4mm of vertical and 0.9mm of
 * horizontal slack.
 *
 * This number, the card box in `PackagingCard`, the column gutter and those
 * four margins move together or not at all: growing any one of them spills a
 * row or a column onto a second page.
 *
 * EVERY pagination path must go through `chunkCards`. Handing the browser one
 * oversized grid is NOT an equivalent way to paginate — when such a grid spans
 * a page boundary the print dialog silently drops a row, so two jobs never get
 * packed and nothing on screen says so (#1651, reproduced on paper at
 * margins: None). The React rewrite lost this chunking and had to have it put
 * back (#1901).
 */
export const CARDS_PER_SHEET = 10;

/** Split a run of cards into sheet-sized pages, in order. */
function chunkCards(entries: PackagingCustomerEntry[]): PackagingCustomerEntry[][] {
  const sheets: PackagingCustomerEntry[][] = [];
  for (let index = 0; index < entries.length; index += CARDS_PER_SHEET) {
    sheets.push(entries.slice(index, index + CARDS_PER_SHEET));
  }
  return sheets;
}

/** The printable grid — width is exactly two cards plus one gutter. */
const GRID_STYLE = {
  // The vertical cut line is a `::before` absolutely positioned in the
  // gutter, so the grid must be its containing block.
  position: 'relative',
  display: 'grid',
  gridTemplateColumns: `repeat(2, ${PACKAGING_CARD_WIDTH})`,
  columnGap: PACKAGING_CARD_COLUMN_GAP,
  rowGap: 0,
  width: `calc(2 * ${PACKAGING_CARD_WIDTH} + ${PACKAGING_CARD_COLUMN_GAP})`,
  margin: '0 auto',
} as const;

export function PackagingSheet({ result, pageBreakPerDriver }: PackagingSheetProps) {
  const { t } = useTranslation();
  const groups = result.groups;
  const entries = groups.flatMap((g) => g.customers);

  if (entries.length === 0) {
    return (
      <p style={{ margin: '24px', textAlign: 'center', fontSize: '10pt' }}>
        {t('packaging.empty')}
      </p>
    );
  }

  // Both options paginate; they differ only in what may share a sheet.
  // Chunking per group keeps a driver's cards off the next driver's paper,
  // and chunking the flat list packs drivers together — but neither ever
  // emits a grid taller than one page.
  const sheets = pageBreakPerDriver
    ? groups.flatMap((group) => chunkCards(group.customers))
    : chunkCards(entries);

  return (
    <>
      <style>{PACKAGING_SHEET_CSS}</style>
      {sheets.map((cards, index) => (
        // `chunkCards` never returns an empty sheet, so the first
        // card's job id is a stable key.
        <div
          key={cards[0]!.job_id}
          className="sheet-grid"
          style={{
            ...GRID_STYLE,
            // Every sheet but the first starts a page. A break on
            // the first prints a blank sheet ahead of the cards.
            ...(index > 0 ? { breakBefore: 'page' } : {}),
          }}
        >
          {cards.map((entry) => (
            <PackagingCard key={entry.job_id} entry={entry} />
          ))}
        </div>
      ))}
    </>
  );
}
